import CustomModal from '@components/CustomModal';
import { Button, StatusChip, Text } from '@fragments';
import Table from '@components/Table';
import { IconAlertCircle, IconClipboardList, IconHandClick, IconReceipt2, IconUser, IconWebhook } from '@tabler/icons-react';
import { UserAvatar } from '@components/Community';

import './styles.scss';

const STATUS_FILTERS = [
    { value: '', label: 'Todos' },
    { value: 'success', label: 'Sucesso', modifier: 'success' },
    { value: 'failed', label: 'Falhou', modifier: 'failed' },
];

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatJson(value) {
    if (!value) return '{}';
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}



function StatusBadge({ status }) {
    if (status === 'success') return <StatusChip.Success text="Sucesso" />;
    if (status === 'failed') return <StatusChip.Error text="Falhou" />;
    return <StatusChip.Default text={status} />;
}

function FilterChips({ statusFilter, onFilterChange }) {
    return (
        <div className="integration-logs-modal__filters">
            {STATUS_FILTERS.map(f => {
                const isActive = statusFilter === f.value;

                return (
                    <Button
                        key={f.value}
                        size="small"
                        color={isActive ? 'inverse' : 'overlay'}
                        onClick={() => onFilterChange(f.value)}
                        left={f.modifier && <span className="filter-dot" />}
                    >
                        {f.label}
                    </Button>
                );
            })}
        </div>
    );
}

function LogDetailEmpty() {
    return (
        <div className="integration-logs-modal__empty-detail">
            <IconHandClick className="empty-icon" />
            <Text.span contrast={7}>Selecione um log para ver os detalhes</Text.span>
        </div>
    );
}

function LogDetail({ log }) {
    if (!log) return <LogDetailEmpty />;

    const isSuccess = log.status === 'success';
    const responseCodeClass = isSuccess
        ? 'integration-logs-modal__response-code--success'
        : 'integration-logs-modal__response-code--error';

    return (
        <div className="integration-logs-modal__detail">
            {/* Header */}
            <div className="integration-logs-modal__detail-header">
                <div>
                    <StatusBadge status={log.status} />
                </div>
                <Text.span contrast={5}>{formatDate(log.timestamps?.created)}</Text.span>
            </div>

            <div className="integration-logs-modal__detail-body">
                {/* User */}
                {log.user && (
                    <div className="integration-logs-modal__detail-section">
                        <Text.h5
                            weight={400}
                            contrast={7}
                            left={<IconUser />}
                        >Usuário</Text.h5>
                        <div className="integration-logs-modal__user-block">
                            <UserAvatar user={log.user} size={34} />
                            <div>
                                <Text.span contrast={8} style={{ fontWeight: 500 }}>{log.user.username || '—'}</Text.span>
                                {log.user.email && (
                                    <Text.span contrast={5} style={{ display: 'block', fontSize: 12 }}>
                                        {log.user.email}
                                    </Text.span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Action */}
                {log.action && (
                    <div className="integration-logs-modal__detail-section">
                        <Text.h5
                            weight={400}
                            contrast={7}
                            left={<IconWebhook />}
                        >Ação</Text.h5>
                        <div className="integration-logs-modal__info-block">
                            <Text.span contrast={7}>{log.action.type}</Text.span>
                            {log.action.config?.webhook_url && (
                                <Text.span contrast={5} maxLines={1} style={{ fontSize: 12, textAlign: 'right', flex: 1 }}>
                                    {log.action.config.webhook_url}
                                </Text.span>
                            )}
                        </div>
                    </div>
                )}

                {/* Payload */}
                {log.payload && (
                    <div className="integration-logs-modal__detail-section">
                        <Text.h5
                            weight={400}
                            contrast={7}
                            left={<IconClipboardList />}
                        >Payload enviado</Text.h5>
                        <pre className="integration-logs-modal__json-block">
                            {formatJson(log.payload)}
                        </pre>
                    </div>
                )}

                {/* Response */}
                <div className="integration-logs-modal__detail-section">
                    <Text.h5
                        weight={400}
                        contrast={7}
                        left={<IconReceipt2 />}
                    >Resposta</Text.h5>
                    <div className="integration-logs-modal__info-block">
                        <Text.span contrast={5} style={{ fontSize: 11 }}>Código</Text.span>
                        <span className={`integration-logs-modal__response-code ${responseCodeClass}`}>
                            {log.response_code ?? '—'}
                        </span>
                    </div>
                    {log.response_body && (
                        <>
                            <Text.span contrast={5} style={{ fontSize: 11 }}>Body recebido</Text.span>
                            <pre className="integration-logs-modal__json-block">
                                {formatJson(log.response_body)}
                            </pre>
                        </>
                    )}
                    {/* Error message */}
                    {log.error_message && (
                        <div className="integration-logs-modal__error-block">
                            <IconAlertCircle size={16} style={{ color: 'var(--error)', flexShrink: 0, marginTop: 1 }} />
                            <Text.span>{log.error_message}</Text.span>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}

export default function IntegrationLogsView({
    open,
    loading,
    logs,
    pagination,
    selectedLog,
    statusFilter,
    handleClose,
    handleFilterChange,
    handlePageChange,
    handleSelectLog,
}) {
    const rowsConfig = [
        {
            title: 'Data',
            grow: true,
            customRender: (item) => (
                <Text.span contrast={6} style={{ fontSize: 12 }}>
                    {formatDate(item.timestamps?.created)}
                </Text.span>
            ),
        },
        {
            title: 'Status',
            align: 'center',
            width: '85px',
            customRender: (item) => <StatusBadge status={item.status} />,
        },
        {
            title: 'Código',
            align: 'center',
            width: '80px',
            customRender: (item) => (
                <Text.span
                    contrast={item.response_code >= 400 ? 7 : 6}
                    style={{ fontWeight: item.response_code >= 400 ? 600 : 400 }}
                >
                    {item.response_code ?? '—'}
                </Text.span>
            ),
        },
    ];

    const bodyContent = (
        <div className="integration-logs-modal">
            {/* Left panel */}
            <div className="integration-logs-modal__left">
                <FilterChips statusFilter={statusFilter} onFilterChange={handleFilterChange} />

                <div className="integration-logs-modal__table-wrap">
                    <Table
                        rowsConfig={rowsConfig}
                        data={logs}
                        loading={loading}
                        loadingRows={6}
                        emptyMessage="Nenhum log encontrado"
                        enablePagination={true}
                        pages={pagination.pages}
                        records={pagination.records}
                        page={pagination.page}
                        onSelectPage={handlePageChange}
                        onRowClick={handleSelectLog}
                        rowClassName={(item) => item.id === selectedLog?.id ? 'log-row--selected' : ''}
                        className="integration-logs-modal__table"
                    />
                </div>
            </div>

            {/* Right panel */}
            <div className="integration-logs-modal__right">
                <LogDetail key={selectedLog?.id} log={selectedLog} />
            </div>
        </div>
    );

    return (
        <CustomModal
            open={open}
            maxWidth={1100}
            title="Logs de execução"
            handleClose={handleClose}
            className="integration-logs-modal-container"
            hideSecondaryButton={false}
            bottomButtonCancelText="Fechar"
            handleSecondary={handleClose}
            bodyWrapperStyle={{ padding: 0, paddingBottom: 0 }}
            bodyComponent={open ? bodyContent : null}
        />
    );
}
